// Layout document types, derived from the DM's real export (2026-09-18).
// The document is one JSON object per profile; unknown keys are preserved
// untouched so nothing the old site wrote is lost when we save.

export type AbilityKey = "str" | "dex" | "con" | "int" | "wis" | "cha";

// A condition with a duration counts down on the affected character's turns:
// "remaining" turns, ending at the start or the end of their turn; no
// remaining means "until removed" (a rest, a save, the DM).
export interface DdbFeatureUse { name: string; used: number; max: number }
// anchorId: whose turn the countdown follows (blank = the affected creature's own turn).
export interface Condition { name?: string; condition?: string; remaining?: number; total?: number; phase?: "start" | "end"; anchorId?: string; anchorName?: string; note?: string; source?: { casterId: string; spell: string; concentration?: boolean }; [k: string]: unknown }

export interface Character {
  id: string;                 // "ddb-<sheet id>" or "custom-<id>"
  initials: string;
  name: string;
  detail: string;             // "Variant Human · Wizard 10"
  hp: number;
  maxHp: number;
  tempHp: number;
  ac: number;
  initiative: number;
  passivePerception: number;
  insight: number;
  investigation: number;
  abilities: Record<AbilityKey, number>;
  savingThrows: Record<AbilityKey, number>;
  proficiencyBonus: number;
  spellSaveDc: number | null;
  spellcasting: unknown[];
  preparedSpells: unknown[];
  timedClassFeatures: unknown[];
  coins: { pp: number; gp: number; ep: number; sp: number; cp: number };
  coinLocations: unknown[];
  inventoryContainers: unknown[];
  inventoryItems: unknown[];
  trainedSkills: unknown[];
  languages: string[];
  toolProficiencies: string[];
  equippedItems: unknown[];
  magicItems: unknown[];
  followers: unknown[];
  dmNoteRecords: unknown[];
  initiativeAdvantage: boolean;
  cannotBeSurprised: boolean;
  exhaustion: number;
  movement: { type: string; feet: number }[];
  vision: { type: string; range: number }[];
  conditions: (string | Condition)[];
  color: string;
  deathSuccesses: number;
  deathFailures: number;
  // The sheet's "Max HP Modifier" (bonusHitPoints) and the part of it the DM
  // Screen applied as drains (life drain etc.), so a long rest can restore it.
  maxHpModifier?: number;
  maxHpReduction?: number;
  // The sheet's own maximum without the modifier (learned from live reads);
  // maxHp = maxHpBase + maxHpModifier is kept true in every direction.
  maxHpBase?: number;
  // Active auras (see state/auras.ts) and the sheet's limited-use counters
  // as last seen, so a feature ticked off on the sheet is noticed.
  auras?: { key: string; name: string; radius: number; remaining: number | null; total?: number; concentration?: boolean; color: string; since: string; roll?: number; rollUnit?: string }[];
  featureUses?: DdbFeatureUse[];
  // Spell slots USED per level, as the live poll last saw them. The maximum is computed from class levels (state/spellSlots.ts).
  spellSlotsUsed?: { level: number; used: number }[];
  // A concentration spell held without an aura (Bless, Hold Person…): its name, for concentration checks.
  concentrationOn?: string;
  // The subclasses D&D Beyond names for this character ("Oath of Devotion",
  // "Twilight Domain"). The live poll reports them; they are kept because a
  // subclass decides which features a character simply HAS — and the sheet's
  // detail line ("Human · Paladin 11") does not say which oath.
  subclasses?: string[];
  // The spells on the D&D Beyond record (state/spellRules.ts) — the gate for spell automation.
  /* Carries the same fields as the D&D Beyond catalogue since 0.8.203, because for a
     PREPARED caster the spellbook is the only complete source: the class-list endpoint
     returns what a character knows, and a cleric knows only cantrips. Bless reaches
     the DM Screen this way or not at all. */
  spellbook?: { id: number; name: string; level: number; concentration: boolean; ritual: boolean; source: string; prepared: boolean; legacy: boolean;
    range?: { origin: string; value: number } | null; aoe?: { type: string; size: number } | null;
    save?: string; attack?: boolean; damage?: { type: string; dice: string; scaling: string }[]; tags?: string[] }[];
  // The light the character carries (state/lighting.ts); lit auras add theirs.
  light?: { key: string; name: string; bright: number; dim: number; remaining: number | null; total?: number; since: string };
  // A gear light the DM put out by hand: its ledger identity (gearDouseKey), so
  // the automatic sweep does not relight an item that is still equipped on the
  // sheet. Cleared once the sheet's lit gear changes. See state/lighting.ts.
  lightDoused?: string;
  revivalPenalty: number;
  effectLevels: Record<string, unknown>;
  [k: string]: unknown;
}

export interface CampaignSnapshot {
  name: string;
  sourceId: string;
  sourceUrl: string;
  rosterSourceIds: string[];
  lastSyncedAt: string;
  characters: Character[];
  [k: string]: unknown;
}

export interface ActivityEntry { time: string; text: string; meta?: string; tone?: string; [k: string]: unknown }

export interface Layout {
  activeCampaign: string;
  activeNav: string;
  selectedId: string;
  characters: Character[];
  campaignSnapshots: CampaignSnapshot[];
  activity: ActivityEntry[];
  viewMode: string;
  railOpen: boolean;
  sidebarWidth: number;
  initiativeScores: Record<string, string>;
  roundNumber: number;
  surpriseRound: boolean;
  [k: string]: unknown;      // ~50 more keys owned by panels not yet ported
}

export const NAV_SECTIONS: { group: string; items: string[] }[] = [
  { group: "", items: ["Party overview"] },
  /* Initiative rides directly under the party: it is the page the DM is on for
     most of a session, and it was buried at the top of Session tools. A second
     group with no label keeps the party roster attached to Party overview
     rather than pushing the roster below it. */
  { group: "", items: ["Initiative"] },
  { group: "Rules reference", items: ["Conditions", "Core rulings", "Actions in combat", "Weapon masteries"] },
  { group: "Campaign rules", items: ["Homebrew rules", "Party inventory", "Magic item workshop", "Circle casting", "Standing & influence", "Factions"] },
  { group: "Session tools", items: ["Spellbook", "Sync health", "Encounter builder", "Session notes", "Random tables", "Spell art"] },
];

export function emptyLayout(): Layout {
  return {
    activeCampaign: "",
    activeNav: "Party overview",
    selectedId: "",
    characters: [],
    campaignSnapshots: [],
    activity: [],
    viewMode: "compact",
    railOpen: true,
    sidebarWidth: 274,
    initiativeScores: {},
    roundNumber: 1,
    surpriseRound: false,
  };
}

export const abilityMod = (score: number) => Math.floor((Number(score) - 10) / 2);
export const signed = (n: number) => (n >= 0 ? `+${n}` : `${n}`);
export const initialsOf = (name: string) => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? "").join("") || "?";
